import { Link } from "react-router-dom";
import { optimizeImage, buildSrcSet } from "@/lib/image";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CarCard({ car }) {
  const videoSrc = car.video_url
    ? (car.video_url.startsWith("http") ? car.video_url : `${BACKEND}${car.video_url}`)
    : null;
  return (
    <Link
      to={`/auto/${car.id}`}
      data-testid={`car-card-${car.id}`}
      className="group block bg-[#0e0e0e] border border-white/10 hover:border-[#22ff66] transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-black">
        {videoSrc ? (
          <video
            src={videoSrc}
            poster={car.images?.[0]}
            muted
            loop
            playsInline
            preload="metadata"
            onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
            onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src={optimizeImage(car.images?.[0] || "https://images.unsplash.com/photo-1503376780353-7e6692767b70", 640)}
            srcSet={buildSrcSet(car.images?.[0])}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 400px"
            alt={`${car.brand} ${car.model}`}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
        )}
        {car.featured && (
          <div className="absolute top-3 left-3 bg-[#22ff66] text-black text-[10px] uppercase tracking-widest px-2 py-1 font-bold">
            In evidenza
          </div>
        )}
        {(car.rent_to_buy || (car.listing_types || []).includes("rent_to_buy")) && (
          <div
            data-testid="card-rtb-badge"
            className="absolute top-3 left-3 flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 font-bold rounded-full border backdrop-blur"
            style={{ background: "rgba(6,214,160,0.18)", borderColor: "rgba(6,214,160,0.6)", color: "#06D6A0", top: car.featured ? "42px" : "12px" }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#06D6A0" }} />
            Rent to Buy
          </div>
        )}
        {!car.is_new && (
          <div className="absolute top-3 right-3 bg-black/70 backdrop-blur text-white text-[10px] uppercase tracking-widest px-2 py-1">
            Usata
          </div>
        )}
        {car.sound_url && (
          <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur text-[#22ff66] text-[10px] uppercase tracking-widest px-2 py-1 flex items-center gap-1.5 border border-[#22ff66]/40">
            <span className="w-1.5 h-1.5 bg-[#22ff66] rounded-full animate-pulse" />
            Engine Sound
          </div>
        )}
        {videoSrc && (
          <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur text-white/90 text-[10px] uppercase tracking-widest px-2 py-1 flex items-center gap-1.5 border border-white/20">
            <span className="w-1.5 h-1.5 bg-white rounded-full" />
            Video
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#22ff66] font-semibold mb-1">{car.brand}</div>
        <h3 className="heading-uc text-lg text-white mb-3 line-clamp-2 lg:line-clamp-1 break-words">{car.model}</h3>

        <div className="grid grid-cols-3 divide-x divide-white/10 border-t border-white/10 pt-3">
          {car.rent_daily ? (
            <div className="text-center">
              <div className="font-display text-base text-white font-bold">€{car.rent_daily}</div>
              <div className="text-[9px] uppercase tracking-wider text-white/50 mt-0.5">al giorno</div>
            </div>
          ) : null}
          {car.rent_weekly ? (
            <div className="text-center">
              <div className="font-display text-base text-white font-bold">€{car.rent_weekly}</div>
              <div className="text-[9px] uppercase tracking-wider text-white/50 mt-0.5">a settimana</div>
            </div>
          ) : null}
          {car.rent_monthly ? (
            <div className="text-center">
              <div className="font-display text-base text-white font-bold">€{car.rent_monthly}</div>
              <div className="text-[9px] uppercase tracking-wider text-white/50 mt-0.5">al mese</div>
            </div>
          ) : null}
        </div>

        {car.listing_type !== "rent" && car.sale_price && (
          <div className="mt-3 pt-3 border-t border-white/10 flex justify-between items-center">
            <span className="text-xs text-white/50 uppercase tracking-wider">Acquisto</span>
            <span className="font-display text-lg text-[#22ff66] font-bold">€{car.sale_price?.toLocaleString("it-IT")}</span>
          </div>
        )}
      </div>
    </Link>
  );
}
